import { useLocation } from 'react-router-dom';

import {
  PATH_COLLABORATION_MISSIONS_NEW,
  PATH_FOLDER_ADDRESS_EDIT,
  PATH_FOLDER_COLLABORATIONS_NEW,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_DELETE,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_FLUSH,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_MODIFY,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_NEW,
  PATH_FOLDER_GENERAL_EDIT,
  PATH_FOLDER_GENERAL_EDIT_CODE,
  PATH_FOLDER_GENERAL_EDIT_STATE,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_DELETE,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_FLUSH,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_MODIFY,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_NEW,
  PATH_FOLDER_MISSIONS_NEW,
} from '../../app/constants/paths.constant';
import { useIdParam } from '../../utils/use-id-param.util';

import { PageGate } from './PageGate';
import { useFolderAccess } from './hooks/useFolderAccess.hook';
import { useOpenedAt } from './hooks/useOpenedAt.hook';
import { requiredScopeOf } from './utils/required-scope-of.util';
import type { FolderAccess } from './hooks/useFolderAccess.hook';

type FolderScope = Exclude<keyof FolderAccess, 'answeredAt'>;

// The scope each edition or creation page requires.
const FOLDER_PAGES_SCOPES: readonly (readonly [string, FolderScope])[] = [
  [PATH_FOLDER_GENERAL_EDIT, 'update'],
  [PATH_FOLDER_GENERAL_EDIT_CODE, 'update'],
  [PATH_FOLDER_GENERAL_EDIT_STATE, 'changeState'],
  [PATH_FOLDER_ADDRESS_EDIT, 'update'],
  [PATH_FOLDER_INTERNAL_ACTORS_EDIT, 'update'],
  [PATH_FOLDER_INTERNAL_ACTORS_EDIT_DELETE, 'update'],
  [PATH_FOLDER_INTERNAL_ACTORS_EDIT_FLUSH, 'update'],
  [PATH_FOLDER_INTERNAL_ACTORS_EDIT_CUSTOMIZE, 'update'],
  [PATH_FOLDER_INTERNAL_ACTORS_EDIT_MODIFY, 'update'],
  [PATH_FOLDER_INTERNAL_ACTORS_EDIT_NEW, 'update'],
  [PATH_FOLDER_EXTERNAL_ACTORS_EDIT, 'update'],
  [PATH_FOLDER_EXTERNAL_ACTORS_EDIT_DELETE, 'update'],
  [PATH_FOLDER_EXTERNAL_ACTORS_EDIT_FLUSH, 'update'],
  [PATH_FOLDER_EXTERNAL_ACTORS_EDIT_CUSTOMIZE, 'update'],
  [PATH_FOLDER_EXTERNAL_ACTORS_EDIT_MODIFY, 'update'],
  [PATH_FOLDER_EXTERNAL_ACTORS_EDIT_NEW, 'update'],
  [PATH_FOLDER_MISSIONS_NEW, 'initMission'],
  [PATH_FOLDER_COLLABORATIONS_NEW, 'initMission'],
  [PATH_COLLABORATION_MISSIONS_NEW, 'initMission'],
];
// The edition and creation pages of a folder, reached directly by their URL, explain why they are
// refused to a user not allowed to use them. The page stays mounted while the answer is pending.
export const FolderPagesGuard = () => {
  const { pathname } = useLocation();
  const openedAt = useOpenedAt(pathname);
  const access = useFolderAccess(useIdParam('folderId'), pathname);
  const scope = requiredScopeOf(FOLDER_PAGES_SCOPES, pathname);

  return (
    <PageGate
      gate={scope ? access[scope] : undefined}
      isFresh={access.answeredAt >= openedAt}
    />
  );
};
